/**
 * Плашки мобильного приложения: «нет сети» и «установите на экран Домой».
 *
 * Обе — поверх интерфейса внизу экрана, не сдвигают раскладку. Подсказка
 * установки показывается только в мобильном браузере (не в установленном
 * приложении и не в настольном) и закрывается навсегда для этого телефона.
 */

import { useEffect, useState } from 'react';
import { Download, Share, WifiOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isDesktop } from '../lib/desktop.ts';
import {
  canPromptInstall,
  isIos,
  isPhoneLike,
  isStandalone,
  onInstallAvailable,
  onStaleData,
  promptInstall,
} from '../lib/pwa.ts';

export function OfflineBanner() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    const off = onStaleData(setStale);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      off();
    };
  }, []);

  if (online && !stale) return null;
  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-3"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + var(--tw-bottom-bar, 0px) + 12px)' }}
    >
      <p className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-heading px-3.5 py-2 text-[12px] font-medium text-card shadow-lg">
        <WifiOff className="size-4" />
        {stale || !online ? 'Нет сети — показаны сохранённые данные' : 'Нет сети'}
      </p>
    </div>
  );
}

const DISMISS_KEY = 'tw.pwa.installHintDismissed';

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function InstallHint() {
  const eligible = !isDesktop() && !isStandalone() && isPhoneLike();
  const [hidden, setHidden] = useState(() => !eligible || dismissed());
  const [androidReady, setAndroidReady] = useState(canPromptInstall);
  /*
   * Chrome сообщает «можно установить» не сразу, а Samsung Internet, Firefox
   * и встроенные браузеры мессенджеров — не всегда. Не дождались за 4 с —
   * показываем, где это в меню браузера, вместо кнопки.
   */
  const [manual, setManual] = useState(false);

  useEffect(() => onInstallAvailable(() => setAndroidReady(true)), []);
  useEffect(() => {
    if (!eligible) return;
    const t = setTimeout(() => setManual(true), 4000);
    return () => clearTimeout(t);
  }, [eligible]);

  const ios = isIos();
  if (hidden || (!ios && !androidReady && !manual)) return null;

  const close = () => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // без хранилища подсказка просто вернётся в следующий раз
    }
  };

  return (
    <div className="fixed inset-x-0 z-[60] px-3" style={{ bottom: 'calc(env(safe-area-inset-bottom) + var(--tw-bottom-bar, 0px) + 12px)' }}>
      <div className="mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-border bg-card p-3.5 shadow-xl">
        <img src="/icons/icon-192.png" alt="" className="size-11 flex-none rounded-xl border border-border" />
        <div className="min-w-0 flex-1 text-[13px]">
          <p className="font-semibold text-heading">BUCOR на экран «Домой»</p>
          {ios ? (
            <p className="mt-0.5 leading-snug text-soft">
              Нажмите <Share className="inline size-3.5 -translate-y-px" /> «Поделиться» внизу Safari, затем «На экран „Домой“». Приложение
              откроется без адресной строки, с уведомлениями.
            </p>
          ) : androidReady ? (
            <p className="mt-0.5 leading-snug text-soft">Откроется как обычное приложение — без адресной строки и с уведомлениями.</p>
          ) : (
            <p className="mt-0.5 leading-snug text-soft">
              Откройте меню браузера <span className="font-semibold">⋮</span> и выберите «Установить приложение» или «Добавить на главный
              экран».
            </p>
          )}
          {!ios && androidReady && (
            <Button size="sm" className="mt-2 h-8 rounded-full" onClick={() => void promptInstall().then((ok) => ok && close())}>
              <Download className="size-4" /> Установить
            </Button>
          )}
        </div>
        <button
          type="button"
          aria-label="Закрыть"
          onClick={close}
          className="grid size-7 flex-none place-items-center rounded-lg border-0 bg-transparent p-0 text-muted-foreground hover:bg-secondary"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
